import { ChangeDetectionStrategy, Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsCameraToolType,
  HsCameraToolTypeControlAction,
  HsGlobal,
  HsGraphicObject,
  HsOutputMessageType,
  HsSubscription,
  HsVector3,
  hsGenerateCubeGeometryData,
  hsMakeGeometry,
  type HsHypersceneViewer,
  type HsMaterial,
  type HsOutputMessageObject,
  type HsRegularGeometry,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makeLitMaterial } from '../../functions/make-lit-material.function';

const CAMERA_POSITION: [number, number, number] = [2, 10, 30];
const CAMERA_LOOK_AT_POSITION: [number, number, number] = [0, 2, 0];
const ROTATION_POINT_DISTANCE: number = 30;
const GROUND_SIZE: number = 40;

/** A red cube on a checkerboard ground with buttons that switch the active camera tool. */
@Component({
  selector: 'hyperscene-500000-items',
  templateUrl: './camera-tools.component.html',
  styleUrls: ['./camera-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class CameraTools extends DemoViewerPage implements OnDestroy {
  public readonly cameraToolTypesMap: Map<HsCameraToolType, string> = new Map([
    [HsCameraToolType.Default, 'Default'],
    [HsCameraToolType.LookAt, 'Look at'],
    [HsCameraToolType.FocusAt, 'Focus at'],
    [HsCameraToolType.TargetFocusObject, 'Rotate around object'],
    [HsCameraToolType.RotateOrbit, 'Rotate around point'],
  ]);

  public selectedCameraToolType: HsCameraToolType | null = null;

  private readonly subscription: HsSubscription = new HsSubscription();
  private readonly pointer: [number, number] = [0, 0];
  private shiftPressed: boolean = false;

  protected initScene(viewer: HsHypersceneViewer): void {
    const geometry: HsRegularGeometry | null = hsMakeGeometry(viewer.surface.graphics, hsGenerateCubeGeometryData());
    if (geometry === null) {
      return;
    }
    HsGlobal.ROTATE_POINT.setFromArray(CAMERA_LOOK_AT_POSITION);

    this.addCube(viewer, geometry, makeLitMaterial(viewer.gl, 1, 0, 0), [0, 2, 0], 2);
    const lightMaterial: HsMaterial = makeLitMaterial(viewer.gl, 1, 1, 1);
    const darkMaterial: HsMaterial = makeLitMaterial(viewer.gl, 0.3, 0.3, 0.3);
    for (let i: number = 0; i < GROUND_SIZE; i++) {
      for (let j: number = 0; j < GROUND_SIZE; j++) {
        const material: HsMaterial = (i + j) % 2 === 1 ? darkMaterial : lightMaterial;
        this.addCube(viewer, geometry, material, [i * 2 - GROUND_SIZE, -2, j * 2 - GROUND_SIZE], 1);
      }
    }
    viewer.camera.worldMatrix.setPosition(CAMERA_POSITION);

    this.selectedCameraToolType = viewer.cameraTools.activeCameraToolType;
    this.changeDetector.markForCheck();
    this.subscription.add(
      HsGlobal.outputMessages.subscribe((message: HsOutputMessageObject) => this.onOutputMessage(message))
    );
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
  }

  public override ngOnDestroy(): void {
    this.subscription.unsubscribe();
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    super.ngOnDestroy();
  }

  public selectCameraTool(cameraToolType: HsCameraToolType): void {
    if (this.viewer === null) {
      return;
    }
    this.selectedCameraToolType = cameraToolType;
    this.viewer.controls.updateControlAction(new HsCameraToolTypeControlAction(cameraToolType));
    this.viewer.requestRender();
  }

  private addCube(
    viewer: HsHypersceneViewer,
    geometry: HsRegularGeometry,
    material: HsMaterial,
    position: [number, number, number],
    scale: number
  ): void {
    const cube: HsGraphicObject = new HsGraphicObject(getUuid(), 'cube', geometry, material);
    cube.worldMatrix.scale([scale, scale, scale]);
    cube.worldMatrix.setPosition(position);
    cube.updateBoundingSphere();
    viewer.add(cube);
  }

  private onOutputMessage(message: HsOutputMessageObject): void {
    // Look at and focus at are one-shot transitions; navigation returns to the default tool afterwards.
    if (
      message.type === HsOutputMessageType.CameraLookAtComplete ||
      message.type === HsOutputMessageType.CameraFocusAtComplete
    ) {
      this.selectCameraTool(HsCameraToolType.Default);
      this.changeDetector.markForCheck();
    }
  }

  private readonly onMouseMove = (event: MouseEvent): void => {
    this.pointer[0] = event.clientX;
    this.pointer[1] = event.clientY;
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Shift' && !this.shiftPressed) {
      this.shiftPressed = true;
      this.setRotationCenterUnderPointer();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (event.key === 'Shift' && this.shiftPressed) {
      this.shiftPressed = false;
      HsGlobal.ROTATE_POINT.setFromArray(CAMERA_LOOK_AT_POSITION);
    }
  };

  /** Holding Shift orbits around a point under the pointer, a fixed distance in front of the camera. */
  private setRotationCenterUnderPointer(): void {
    const canvas: HTMLCanvasElement | undefined = this.canvasDocument?.nativeElement;
    if (this.viewer === null || canvas === undefined) {
      return;
    }
    const camera: HsHypersceneViewer['camera'] = this.viewer.camera;
    const { left, top, width, height }: DOMRect = canvas.getBoundingClientRect();
    const deviceX: number = ((this.pointer[0] - left) / width) * 2 - 1;
    const deviceY: number = 1 - ((this.pointer[1] - top) / height) * 2;
    const rotationPoint: HsVector3 = HsVector3.makeZeroVector().setFromMatrixPosition(camera.worldMatrix.values);
    const direction: HsVector3 = HsVector3.makeZeroVector()
      .set(deviceX, deviceY, 0.5)
      .unproject(camera.projectionMatrixInverse.values, camera.worldMatrix.values)
      .subtract(rotationPoint)
      .normalize()
      .multiplyByScalar(ROTATION_POINT_DISTANCE);
    HsGlobal.ROTATE_POINT.copy(rotationPoint.add(direction));
  }
}
